/**
 * Tender Radar — Bid Intelligence
 *
 * Scans procurement portals & web for tenders matching RESILIGENT's services.
 * Scores strategic fit, maps products, flags escalations.
 */

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Target, TrendingUp, ExternalLink,
  Plus, ChevronRight, X, Clock, AlertTriangle,
  BarChart3, Filter, RefreshCw, CheckCircle2,
  FileText, Zap, Building2, Globe, Shield, Database,
  ArrowRight, ThumbsUp, Eye, XCircle, Loader2, Radio,
} from "lucide-react";

const FIT_CONFIG = {
  HIGH:   { label: "High Fit",   color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  MEDIUM: { label: "Medium Fit", color: "bg-amber-100 text-amber-700 border-amber-300" },
  LOW:    { label: "Low Fit",    color: "bg-slate-100 text-slate-500 border-slate-300" },
};

const STATUS_CONFIG = {
  new:       { label: "New",       color: "bg-blue-100 text-blue-700" },
  reviewing: { label: "Reviewing", color: "bg-amber-100 text-amber-700" },
  pursuing:  { label: "Pursuing",  color: "bg-emerald-100 text-emerald-700" },
  submitted: { label: "Submitted", color: "bg-violet-100 text-violet-700" },
  won:       { label: "Won",       color: "bg-green-100 text-green-700" },
  lost:      { label: "Lost",      color: "bg-red-100 text-red-700" },
  expired:   { label: "Expired",   color: "bg-slate-100 text-slate-400" },
  dismissed: { label: "Dismissed", color: "bg-slate-100 text-slate-400" },
};

function StatCard({ icon, label, value }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <p className="text-lg font-bold text-slate-800">{value ?? 0}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TenderRadar() {
  const [tenders, setTenders] = useState([]);
  const [checkDeadline, setCheckDeadline] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [filterCountry, setFilterCountry] = useState("");
  const [filterFit, setFilterFit] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showLowFit, setShowLowFit] = useState(false);
  const [vendorRegs, setVendorRegs] = useState([]);
  const [regAlerts, setRegAlerts] = useState([]);
  const [tenderSources, setTenderSources] = useState(null);
  const [activeSection, setActiveSection] = useState("tenders"); // tenders, sources, registrations

  // ─── Data Fetching ──────────────────────────────────────────

  const fetchTenders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterCountry) params.set("country", filterCountry);
      if (filterFit) params.set("fit", filterFit);
      if (filterStatus) params.set("status", filterStatus);
      if (showLowFit) params.set("include_low", "true");

      const [tendersRes, statsRes, regsRes, sourcesRes] = await Promise.all([
        fetch(`${API}/tenders?${params}`, { credentials: "include" }),
        fetch(`${API}/tenders/stats`, { credentials: "include" }),
        fetch(`${API}/tenders/vendor-registrations`, { credentials: "include" }),
        fetch(`${API}/tenders/sources`, { credentials: "include" }),
      ]);

      if (tendersRes.ok) {
        const data = await tendersRes.json();
        setTenders(data.tenders || []);
        setCheckDeadline(data.check_deadline || []);
      }
      if (regsRes.ok) {
        const data = await regsRes.json();
        setVendorRegs(data.registrations || []);
        setRegAlerts(data.alerts || []);
      }
      if (sourcesRes.ok) setTenderSources(await sourcesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {
      toast.error("Failed to load tenders");
    } finally {
      setLoading(false);
    }
  }, [filterCountry, filterFit, filterStatus, showLowFit]);

  useEffect(() => { fetchTenders(); }, [fetchTenders]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/tenders/scan`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "started") {
          toast.success("Scanning all sources (World Bank, AfDB, IFC, CBN, Banks, DFIs) — this may take 2-3 minutes...");
          setTimeout(fetchTenders, 30000);
          setTimeout(fetchTenders, 60000);
          setTimeout(fetchTenders, 120000);
        } else if (data.status === "already_running") {
          toast.info("A scan is already in progress");
        }
      }
    } catch {
      toast.error("Failed to start tender scan");
    } finally {
      setScanning(false);
    }
  };

  const handlePushToMC = async (tenderId) => {
    try {
      const res = await fetch(`${API}/tenders/push-to-mission-control`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tender_id: tenderId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Pushed to Mission Control as signal: ${data.signal_id}`);
        fetchTenders();
      } else {
        toast.error("Failed to push");
      }
    } catch { toast.error("Failed"); }
  };

  const handleInterest = async (tenderId, interest) => {
    try {
      const res = await fetch(`${API}/tenders/${tenderId}/interest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interest }),
      });
      if (res.ok) {
        toast.success(`Marked as: ${interest}`);
        // Immediately remove dismissed tenders from view
        if (interest === "dismiss") {
          setTenders(prev => prev.filter(t => t.tender_id !== tenderId));
          if (selectedTender?.tender_id === tenderId) setSelectedTender(null);
        } else {
          fetchTenders();
          if (selectedTender?.tender_id === tenderId) {
            const updated = await fetch(`${API}/tenders/${tenderId}`, { credentials: "include" });
            if (updated.ok) setSelectedTender(await updated.json());
          }
        }
      }
    } catch {
      toast.error("Failed to update interest");
    }
  };

  const formatDate = (d) => {
    if (!d) return "No deadline";
    try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return d; }
  };

  const daysUntilDeadline = (d) => {
    if (!d) return null;
    return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const uniqueCountries = [...new Set(tenders.map(t => t.country).filter(Boolean))].sort();

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="p-6" data-testid="tender-radar-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <FileText className="w-5 h-5 text-blue-600" />
            <h1 className="text-xl font-bold tracking-tight text-slate-800">Tender Radar</h1>
            <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600 ml-1">BID INTELLIGENCE</Badge>
          </div>
          <p className="text-sm text-slate-500">
            Daily procurement scanning — leadership, coaching, OD & culture transformation opportunities
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-slate-300" onClick={handleScan} disabled={scanning} data-testid="scan-tenders-btn">
            {scanning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            {tenders.length === 0 ? "Scan for Tenders" : "Scan for New Tenders"}
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowAdd(true)} data-testid="add-tender-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Tender
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="tender-stats">
          <StatCard icon={<FileText className="w-4 h-4 text-blue-600" />} label="Total Tenders" value={stats.total} />
          <StatCard icon={<Target className="w-4 h-4 text-emerald-600" />} label="High Fit" value={stats.by_fit?.HIGH || 0} />
          <StatCard icon={<TrendingUp className="w-4 h-4 text-amber-600" />} label="Medium Fit" value={stats.by_fit?.MEDIUM || 0} />
          <StatCard icon={<AlertTriangle className="w-4 h-4 text-red-600" />} label="Escalation Required" value={stats.escalation_required} />
          <StatCard icon={<Clock className="w-4 h-4 text-violet-600" />} label="Deadline < 14d" value={stats.approaching_deadlines} />
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-4" data-testid="tender-sections">
        {[
          { id: "tenders", label: "Tenders", count: tenders.length },
          { id: "sources", label: `Sources (${tenderSources?.total_sources || 0})` },
          { id: "registrations", label: `Registrations${regAlerts.length > 0 ? ` (${regAlerts.length} alert${regAlerts.length > 1 ? "s" : ""})` : ""}` },
        ].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeSection === s.id ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`section-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Registration Alerts */}
      {regAlerts.length > 0 && activeSection === "tenders" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="reg-alerts">
          <p className="text-xs font-bold text-amber-700 mb-1">Vendor Registration Alerts</p>
          {regAlerts.map((a, i) => (
            <p key={i} className="text-xs text-amber-600">{a.alert}</p>
          ))}
        </div>
      )}

      {/* ═══ SOURCES SECTION ═══ */}
      {activeSection === "sources" && tenderSources && (
        <div className="space-y-4" data-testid="sources-panel">
          {Object.entries(tenderSources.sources).map(([category, sources]) => (
            <div key={category} className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2 capitalize">{category.replace(/_/g, " ")}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {sources.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded bg-slate-50 border border-slate-100">
                    <div className={`w-2 h-2 rounded-full ${s.status === "active" ? "bg-green-500" : "bg-amber-500"}`} />
                    <span className="text-xs text-slate-700">{s.name}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{s.type}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ REGISTRATIONS SECTION ═══ */}
      {activeSection === "registrations" && (
        <div className="space-y-3" data-testid="registrations-panel">
          {vendorRegs.map((reg, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{reg.platform}</p>
                <p className="text-xs text-slate-500">{reg.registration_type?.replace(/_/g, " ")} | {reg.url}</p>
                {reg.expiry_date && <p className="text-xs text-amber-600 mt-1">Expires: {reg.expiry_date}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${reg.registered ? "bg-green-50 text-green-700 border-green-300" : "bg-red-50 text-red-600 border-red-300"}`}>
                  {reg.registered ? "Registered" : "Not Registered"}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ TENDERS SECTION ═══ */}
      {activeSection === "tenders" && (
      <>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="tender-filters">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Filter className="w-3.5 h-3.5" /> Filters:
        </div>
        <Select value={filterCountry} onValueChange={(v) => setFilterCountry(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-country">
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {uniqueCountries.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filterFit} onValueChange={(v) => setFilterFit(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-fit">
            <SelectValue placeholder="All Fit Levels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Fit Levels</SelectItem>
            <SelectItem value="HIGH">High Fit</SelectItem>
            <SelectItem value="MEDIUM">Medium Fit</SelectItem>
            <SelectItem value="LOW">Low Fit</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-all ${
            showLowFit ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
          }`}
          onClick={() => setShowLowFit(!showLowFit)}
          data-testid="toggle-low-fit"
        >
          {showLowFit ? "Hide Low Fit" : "Show Low Fit"}
        </button>
      </div>

      {/* Content: Split View */}
      <div className="flex gap-4" style={{ minHeight: "calc(100vh - 300px)" }}>
        {/* Left: Tender List */}
        <div className={`${selectedTender ? "w-1/2" : "w-full"} space-y-2 overflow-y-auto pr-1`} style={{ maxHeight: "calc(100vh - 300px)" }}>
          {!loading && checkDeadline.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-2" data-testid="tenders-check-deadline">
              <p className="text-xs font-semibold text-amber-800 mb-1">Check the deadline ({checkDeadline.length}) — no closing date found; not shown as open or new</p>
              <ul className="space-y-1">
                {checkDeadline.map((t) => (
                  <li key={t.tender_id} className="text-xs text-slate-700" data-testid={`check-deadline-${t.tender_id}`}>
                    <span className="font-semibold">{t.title}</span>
                    <span className="text-slate-500"> — {t.country || "?"} · {t.issuing_organisation || "unknown issuer"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : tenders.length === 0 ? (
            <div className="text-center py-16 text-slate-400" data-testid="tenders-empty">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No open tenders in your countries</p>
              <p className="text-xs mt-1">Click "Scan for Tenders" to search procurement portals</p>
            </div>
          ) : (
            tenders.map((tender) => {
              const fit = tender.strategic_fit || {};
              const fitCfg = FIT_CONFIG[fit.fit] || FIT_CONFIG.LOW;
              const statusCfg = STATUS_CONFIG[tender.status] || STATUS_CONFIG.new;
              const days = daysUntilDeadline(tender.deadline);
              const isSelected = selectedTender?.tender_id === tender.tender_id;

              return (
                <div
                  key={tender.tender_id}
                  data-testid={`tender-card-${tender.tender_id}`}
                  className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
                    isSelected ? "border-blue-400 bg-blue-50/50 ring-1 ring-blue-200" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  onClick={() => setSelectedTender(tender)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${fitCfg.color}`}>
                          {fit.score || 0}
                        </span>
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                        {tender.escalation?.requires_escalation && (
                          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 flex items-center gap-0.5">
                            <Shield className="w-2.5 h-2.5" /> Escalation
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium text-slate-800 line-clamp-2">{tender.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                        {tender.source_label && tender.source_label !== "Web Search" && (
                          <span className="flex items-center gap-0.5 text-blue-600 font-medium"><Database className="w-2.5 h-2.5" />{tender.source_label}</span>
                        )}
                        {tender.issuing_organisation && (
                          <span className="flex items-center gap-0.5"><Building2 className="w-2.5 h-2.5" />{tender.issuing_organisation}</span>
                        )}
                        {tender.country && (
                          <span className="flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />{tender.country}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {days !== null && days >= 0 ? (
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${
                          days <= 7 ? "bg-red-100 text-red-700" : days <= 14 ? "bg-amber-100 text-amber-700" : "text-slate-500"
                        }`}>
                          {days}d left
                        </span>
                      ) : tender.deadline ? (
                        <span className="text-[9px] text-slate-400">Expired</span>
                      ) : (
                        <span className="text-[9px] text-slate-300">No deadline</span>
                      )}
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 mt-1 ml-auto" />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right: Detail Panel */}
        {selectedTender && (
          <div className="w-1/2 border border-slate-200 rounded-lg bg-white overflow-y-auto" style={{ maxHeight: "calc(100vh - 300px)" }}>
            <TenderDetailPanel
              tender={selectedTender}
              onClose={() => setSelectedTender(null)}
              onInterest={handleInterest}
              onPushToMC={handlePushToMC}
              formatDate={formatDate}
              daysUntilDeadline={daysUntilDeadline}
            />
          </div>
        )}
      </div>

      </>
      )}

      {/* Add Tender Modal */}
      {showAdd && (
        <AddTenderModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); fetchTenders(); }}
        />
      )}
    </div>
  );
}


// ─── Detail Panel ─────────────────────────────────────────────

function TenderDetailPanel({ tender, onClose, onInterest, onPushToMC, formatDate, daysUntilDeadline }) {
  const fit = tender.strategic_fit || {};
  const fitCfg = FIT_CONFIG[fit.fit] || FIT_CONFIG.LOW;
  const days = daysUntilDeadline(tender.deadline);

  return (
    <div className="p-5" data-testid="tender-detail-panel">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${fitCfg.color}`}>
              {fit.fit} FIT — {fit.score}
            </span>
            {tender.escalation?.requires_escalation && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Founder Escalation Required
              </span>
            )}
          </div>
          <h2 className="text-sm font-bold text-slate-800">{tender.title}</h2>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="close-detail-btn">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* Key Info */}
      <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
        {tender.source_label && (
          <div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Source</p>
            <p className="font-medium text-blue-600">{tender.source_label}</p>
          </div>
        )}
        {tender.issuing_organisation && (
          <div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Organisation</p>
            <p className="font-medium text-slate-700">{tender.issuing_organisation}</p>
          </div>
        )}
        {tender.country && (
          <div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Country</p>
            <p className="font-medium text-slate-700">{tender.country}</p>
          </div>
        )}
        <div>
          <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Deadline</p>
          <p className={`font-medium ${days !== null && days <= 7 ? "text-red-600" : "text-slate-700"}`}>
            {formatDate(tender.deadline)}
            {days !== null && days >= 0 && <span className="text-slate-400 ml-1">({days}d)</span>}
          </p>
        </div>
        {tender.estimated_value && (
          <div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Est. Value</p>
            <p className="font-medium text-slate-700">{tender.estimated_value}</p>
          </div>
        )}
        {tender.procurement_type && (
          <div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Type</p>
            <p className="font-medium text-slate-700">{tender.procurement_type}</p>
          </div>
        )}
        {tender.tender_reference && (
          <div>
            <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Reference</p>
            <p className="font-medium text-slate-700">{tender.tender_reference}</p>
          </div>
        )}
      </div>

      {/* Description */}
      {tender.description && (
        <div className="mb-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Scope Summary</p>
          <p className="text-xs text-slate-600 leading-relaxed">{tender.description}</p>
        </div>
      )}

      {/* Strategic Fit Reasons */}
      {fit.reasons?.length > 0 && (
        <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Why This Scores {fit.fit}</p>
          <ul className="space-y-1">
            {fit.reasons.map((r, i) => (
              <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Product Alignment */}
      {tender.product_alignment?.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">RESILIGENT Product Alignment</p>
          <div className="flex flex-wrap gap-1.5">
            {tender.product_alignment.map((p) => (
              <span key={p} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Escalation Triggers */}
      {tender.escalation?.requires_escalation && tender.escalation.triggers?.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-100">
          <p className="text-[10px] text-red-500 uppercase tracking-wider mb-1.5 font-semibold">Escalation Triggers</p>
          <ul className="space-y-1">
            {tender.escalation.triggers.map((t, i) => (
              <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommended Action */}
      {tender.recommended_action && (
        <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
          <p className="text-[10px] text-amber-600 uppercase tracking-wider mb-1">Rex Recommendation</p>
          <p className="text-xs font-medium text-amber-800">{tender.recommended_action}</p>
        </div>
      )}

      {/* Source Link */}
      {tender.url && (
        <a
          href={tender.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 mb-4"
          data-testid="tender-source-link"
        >
          <ExternalLink className="w-3 h-3" /> View Original Source
        </a>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
          onClick={() => onInterest(tender.tender_id, "pursue")}
          data-testid="pursue-btn"
        >
          <ThumbsUp className="w-3 h-3 mr-1.5" /> Pursue
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8"
          onClick={() => onInterest(tender.tender_id, "monitor")}
          data-testid="monitor-btn"
        >
          <Eye className="w-3 h-3 mr-1.5" /> Monitor
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 text-slate-400"
          onClick={() => onInterest(tender.tender_id, "dismiss")}
          data-testid="dismiss-btn"
        >
          <XCircle className="w-3 h-3 mr-1.5" /> Dismiss
        </Button>
        {!tender.pushed_to_mc && (
          <Button size="sm" variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50"
            onClick={() => onPushToMC(tender.tender_id)}
            data-testid="push-to-mc-btn">
            <Radio className="w-3 h-3 mr-1.5" /> Push to Mission Control
          </Button>
        )}
        {tender.pushed_to_mc && (
          <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-300 text-xs">
            In Mission Control
          </Badge>
        )}
      </div>
    </div>
  );
}


// ─── Add Tender Modal ─────────────────────────────────────────

function AddTenderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", issuing_organisation: "",
    country: "", deadline: "", estimated_value: "",
    url: "", procurement_type: "RFP",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/tenders/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success("Tender added");
        onCreated();
      } else {
        toast.error("Failed to add tender");
      }
    } catch {
      toast.error("Failed to add tender");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="add-tender-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-slate-100 rounded">
          <X className="w-4 h-4 text-slate-400" />
        </button>
        <h2 className="text-base font-bold text-slate-800 mb-4">Add Tender Manually</h2>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">Title *</label>
            <Input
              data-testid="input-title"
              placeholder="e.g. Leadership Development Framework — AfDB"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">Description</label>
            <Input
              data-testid="input-description"
              placeholder="Scope summary"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Organisation</label>
              <Input
                data-testid="input-org"
                placeholder="e.g. African Development Bank"
                value={form.issuing_organisation}
                onChange={(e) => setForm({ ...form, issuing_organisation: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Country</label>
              <Input
                data-testid="input-country"
                placeholder="e.g. Nigeria"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Deadline</label>
              <Input
                data-testid="input-deadline"
                type="date"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Est. Value</label>
              <Input
                data-testid="input-value"
                placeholder="e.g. $200,000"
                value={form.estimated_value}
                onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">Source URL</label>
            <Input
              data-testid="input-url"
              placeholder="https://..."
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="save-tender-btn">
            {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Plus className="w-3 h-3 mr-1.5" />}
            Add Tender
          </Button>
        </div>
      </div>
    </div>
  );
}
